import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { addInteractionEvent } from '@creditornot/browser-telemetry';
import { Button } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Modal } from '@creditornot/cbt-modal';
import { WoltInboundLoadItem } from 'common/api-types/warehouse';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { InvalidSearchQuery } from 'common/components/cookbook/InvalidSearchQuery';
import { ModalContent } from 'common/components/cookbook/Modals/ModalContent';
import { ModalFooter } from 'common/components/cookbook/Modals/ModalFooter';
import { ModalHeader } from 'common/components/cookbook/Modals/ModalHeader';
import { PageSpinner } from 'common/components/cookbook/PageSpinner';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { usePageView } from 'common/hooks/usePageView';
import { useSetSearch } from 'common/hooks/useSetSearch';
import { Item } from 'features/warehouse/common/components/Item';
import { NoItemsFoundLegacy } from 'features/warehouse/common/components/NoItemsFoundLegacy';
import { ScanItemPromptLegacy } from 'features/warehouse/common/components/ScanItemPromptLegacy';
import { WarehousePageApiError } from 'features/warehouse/common/components/WarehouseApiErrorNotification';
import {
  useWarehouseItemSearchEvent,
  WarehouseSearchEventAttributes,
} from 'features/warehouse/common/hooks/telemetry';
import { useInventoryServiceFeatureEnabled } from 'features/warehouse/common/hooks/useWarehouseFeatureEnabled';
import { useWoltItemsQuery } from 'features/warehouse/common/query';
import { isValidSearch } from 'features/warehouse/common/utils/isValidSearch';

import { receivingRoute } from '../..';
import { InboundLoadResults } from './InboundLoadResults/InboundLoadResults';
import { useAutomaticInboundLoadItemSelection } from '../../hooks';
import { useInboundLoadsQuery } from '../../query';

const Header = styled(ContentContainer)`
  border-bottom: 1px solid ${color.border};
`;

const StyledModalFooter = styled(ModalFooter)`
  justify-content: flex-end;
  gap: 16px;
`;

const NoResultsContainer = styled(ContentContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding-top: 32px;
  padding-bottom: 32px;
`;

const NoResultsTitle = styled(T.subheadline)`
  display: block;
  margin-top: 16px;
  color: ${color.textSubdued};
`;

const NoResultsDescription = styled.div`
  margin-top: 16px;
`;

const NoSearchResults: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
}) => {
  return (
    <NoResultsContainer>
      <Icon source={icons.magnifyingGlass} size="medium" fill={color.text} />
      <NoResultsTitle>{title}</NoResultsTitle>
      {description && <NoResultsDescription>{description}</NoResultsDescription>}
    </NoResultsContainer>
  );
};

const IconContainer = styled.div`
  margin-right: 8px;
`;

type Props = {
  selectedWarehouseId: string;
  searchQuery: string;
  isSearchingByGtin: boolean;
  isAutomaticNavigationDisabled: boolean;
};

export const InboundLoadSearch = ({
  selectedWarehouseId,
  searchQuery,
  isSearchingByGtin,
  isAutomaticNavigationDisabled,
}: Props) => {
  const formatMessage = useFormatMessage();

  const inventoryServiceEnabled = useInventoryServiceFeatureEnabled();

  const { confirmItem } = receivingRoute.useSearch();

  const setSearch = useSetSearch(receivingRoute);

  const {
    data: inboundLoadsData,
    isLoading: isLoadingInboundLoads,
    error: inboundLoadsError,
    isFetching: isFetchingInboundLoads,
  } = useInboundLoadsQuery(selectedWarehouseId, searchQuery, {
    refetchInterval: 60_000,
    enabled: !!selectedWarehouseId && (!searchQuery.length || isValidSearch(searchQuery)),
  });

  const isActiveLoadingInboundLoads = isLoadingInboundLoads && isFetchingInboundLoads;

  const inboundLoadItems = useMemo(
    () => inboundLoadsData?.flatMap((inboundLoad) => inboundLoad.items),
    [inboundLoadsData],
  );

  const {
    data: itemsData,
    error: itemsError,
    isLoading: isLoadingItems,
    isFetching: isFetchingItems,
  } = useWoltItemsQuery(
    selectedWarehouseId,
    { q: searchQuery },
    {
      enabled: isValidSearch(searchQuery) && !inventoryServiceEnabled,
    },
  );

  const isActiveLoadingItems = isLoadingItems && isFetchingItems;

  usePageView(
    'receiving',
    {
      wm_venue_id: selectedWarehouseId,
      purchase_order_count: inboundLoadsData?.length,
    },
    { disabled: !inboundLoadsData },
  );

  const searchEvent: WarehouseSearchEventAttributes = {
    view: 'receiving',
    searchQuery,
    resultCount: itemsData?.items.length,
    isSearchingByGtin,
    warehouseId: selectedWarehouseId,
    purchaseOrderCount: inboundLoadsData?.length,
  };

  useWarehouseItemSearchEvent(searchEvent, { disabled: !inboundLoadsData });

  const navigate = useNavigate();

  const handleItemSelection = useCallback(
    (itemId: string, inboundLoadId?: string) => {
      void navigate({
        to: `/receiving/warehouse/$warehouseId/$itemId`,
        params: { itemId, warehouseId: selectedWarehouseId },
        search: (prev: { [key: string]: any }) => ({
          inboundLoadId,
          searchQuery: prev?.searchQuery,
        }),
      });
    },
    [navigate, selectedWarehouseId],
  );

  const handleInboundLoadItemSelection = useCallback(
    ({ itemId, inboundLoadId }: WoltInboundLoadItem) => handleItemSelection(itemId, inboundLoadId),
    [handleItemSelection],
  );

  useAutomaticInboundLoadItemSelection(
    inboundLoadItems,
    searchQuery,
    handleInboundLoadItemSelection,
    isAutomaticNavigationDisabled,
  );

  if (searchQuery && !isValidSearch(searchQuery)) {
    return <InvalidSearchQuery />;
  }

  if (isActiveLoadingInboundLoads || isActiveLoadingItems) {
    return <PageSpinner />;
  }

  if (inboundLoadsError || itemsError) {
    return <WarehousePageApiError error={inboundLoadsError || itemsError} />;
  }

  if (searchQuery.length === 0 && !inboundLoadsData?.length) {
    return <ScanItemPromptLegacy title={formatMessage('receiving.start_scan')} />;
  }

  if (searchQuery.length === 0 && inboundLoadsData?.length) {
    return (
      <InboundLoadResults
        inboundLoads={inboundLoadsData}
        onItemClick={(itemId, inboundLoadId) => {
          handleItemSelection(itemId, inboundLoadId);
        }}
      />
    );
  }

  if (inboundLoadsData) {
    const numberOfInboundLoadItemsFound = inboundLoadItems?.length || 0;

    const itemsNotInPurchaseOrder = (itemsData?.items || []).filter(
      (item) =>
        !(inboundLoadItems || []).find((inboundLoadItem) => inboundLoadItem.itemId === item.id),
    );

    const numberOfItemsFound = itemsNotInPurchaseOrder?.length;

    const handleDismiss = () => {
      setSearch({ confirmItem: '' });
    };

    if (numberOfInboundLoadItemsFound === 0 && numberOfItemsFound === 0) {
      return <NoItemsFoundLegacy isSearchingByGtin={isSearchingByGtin} />;
    }

    return (
      <div style={{ overflow: 'auto' }}>
        {numberOfInboundLoadItemsFound > 0 ? (
          <InboundLoadResults
            inboundLoads={inboundLoadsData}
            onItemClick={(itemId, inboundLoadId) => {
              handleItemSelection(itemId, inboundLoadId);
            }}
            itemSearchResult={true}
          />
        ) : (
          <NoSearchResults
            title={formatMessage('receiving.no_items_with_shipment_found_title')}
            description={formatMessage('receiving.no_items_with_shipment_found_description')}
          />
        )}
        {!!numberOfItemsFound && (
          <div>
            <Header verticalPadding="sm">
              <T.Heading variant="headline" level={2}>
                {formatMessage('receiving.items_without_purchase_order')}
              </T.Heading>
            </Header>

            {itemsNotInPurchaseOrder?.map((item, index) => {
              const { id, name, gtin, image } = item;
              return (
                <Item
                  key={id}
                  name={name}
                  gtin={gtin}
                  image={image}
                  onClick={() => {
                    handleItemSelection(id);
                    addInteractionEvent('receiving', 'click', {
                      wm_venue_id: selectedWarehouseId,
                      item_index: index,
                      item_id: id,
                      gtin,
                      source: 'search_results',
                    });
                  }}
                  bottomContent={
                    <>
                      <IconContainer>
                        <Icon source={icons.arrowRight} size="small" fill={color.text} />
                      </IconContainer>
                      <T.body>
                        {item.location?.name ?? formatMessage('common.not_available')}
                      </T.body>
                    </>
                  }
                />
              );
            })}
          </div>
        )}
        <Modal
          open={!!confirmItem}
          onDismiss={handleDismiss}
          sizePreset="centered:medium"
          disableContentPadding={true}
        >
          <ModalHeader onDismiss={handleDismiss}>
            {formatMessage('receiving.receive_item_without_shipment_title')}
          </ModalHeader>
          <ModalContent>
            <T.body>{formatMessage('receiving.receive_item_without_shipment_explanation')}</T.body>
            <T.body style={{ marginTop: 16, display: 'block' }}>
              {formatMessage('receiving.receive_item_without_shipment_confirmation')}
            </T.body>
          </ModalContent>
          <StyledModalFooter>
            <Button
              size="small"
              variant="secondary"
              onClick={() => {
                setSearch({ confirmItem: '' });
              }}
            >
              {formatMessage('common.cancel')}
            </Button>
            <Button
              data-test-id="continue-button"
              size="small"
              variant="primary"
              type="submit"
              onClick={() => {
                if (confirmItem) {
                  handleItemSelection(confirmItem);
                }
              }}
            >
              {formatMessage('common.continue')}
            </Button>
          </StyledModalFooter>
        </Modal>
      </div>
    );
  }

  return null;
};
